import React from "react";
import { useSnapshot } from "valtio";
import { ToastContainer } from "react-toastify";
import { useTranslation } from "react-i18next";
import { StyledBreadcrumb } from "@aureskonnect/react-ui";
import { DynamicTable } from "@maherunlocker/custom-react-table";

import { store } from "@components/VerticalLayout/store";
import {
    store as localStore,
    setActions,
    setIsActionsClicked,
    setIsEdited,
    setKey,
    setValidationAction,
} from "./store";

import PageTitle from "@components/Common/PageTitle";
import { FoodCourtForm } from "./FoodCourtForm";
import MessageInfo from "@components/Common/MessageInfo";
import { ConfirmationUpdatingDataMessageModal } from "@components/Common/ConfirmationUpdatingDataMessageModal";
import { useCallbackPrompt } from "@hooks/useCallbackPrompt";
import { ArrayOfCustomColumns } from "./ArrayOfCustomColumns";
import { useSelector } from "react-redux";
import { HoursModal } from "./HoursModal";
import { useAuthHeader } from "../../../api/headersKeycloak";

export function FoodCourt() {
    const { oneShop, shopID } = useSnapshot(store);
    const {
        key,
        isActionsClicked,
        validationAction,
        actions,
        isEdited,
    } = useSnapshot(localStore);
    const { t } = useTranslation();
    const getAuthHeader = useAuthHeader();
    const selectedAccount = useSelector(
        (state: any) => state.global.selectedAccount
    );
    const i18nextLng = localStorage.getItem("i18nextLng") || "fr";
    const [showPrompt, confirmNavigation, cancelNavigation] = useCallbackPrompt(
        isEdited
    );

    const [isAddFoodCourtClicked, setIsAddFoodCourtClicked] = React.useState<
        boolean
    >(false);
    const [, setDataIsUpdated] = React.useState<boolean>(false);
    const [filterActive, setLocalFilterActive] = React.useState<boolean>(false);
    const [rowData, setRowData] = React.useState<any>({});
    const [selectedRows, setSelectedRows] = React.useState<any>([]);
    const [isConsult, setIsConsult] = React.useState<boolean>(false);
    const [isModalOpened, setIsModalOpened] = React.useState<boolean>(false);

    let arrayOfCustomColumns = ArrayOfCustomColumns({
        setRowData,
        setIsAddFoodCourtClicked,
        setLocalFilterActive,
        setIsModalOpened,
    });

    React.useEffect(() => {
        setActions(confirmNavigation);
        setIsActionsClicked(showPrompt as boolean);
        setValidationAction(cancelNavigation);
    }, [showPrompt, confirmNavigation, cancelNavigation]);
    React.useEffect(() => {
        setKey(Math.floor(Math.random() * 10000));
    }, [i18nextLng]);

    return (
        <React.Fragment>
            <ToastContainer />
            <PageTitle title={t("Food court")} />
            <div className="page-content__clz ">
                <div className="mdi_txt_informationModes">
                    <StyledBreadcrumb
                        items={[
                            {
                                item: "Settings".toUpperCase(),
                                link: `${process.env.REACT_APP_PORTAIL_URL}`,
                            },
                            {
                                item: selectedAccount?.toUpperCase() || "",
                                link: oneShop
                                    ? "/setting/customer-account"
                                    : "/compte",
                            },
                            {
                                item: t("General settings").toUpperCase(),
                                link: "#",
                            },
                            {
                                item: t("Food court").toUpperCase(),
                                link: "#",
                            },
                        ]}
                    />
                </div>
                <MessageInfo message={t("configuring food court")} />
                <div
                    style={{
                        display: "grid",
                        minHeight: oneShop ? "650px" : "600px",
                        gridTemplateColumns: isAddFoodCourtClicked
                            ? "2fr 1fr "
                            : "auto",
                        gridColumnGap: "4px",
                        border: 0,
                    }}
                >
                    <DynamicTable
                        key={key}
                        requestHeader={{
                            "Accept-Language": i18nextLng,
                            ...getAuthHeader,
                        }}
                        url={`${process.env.REACT_APP_API_V2_URL}/settings/general/foodCourt?shopId=${shopID}`}
                        canExpand={!oneShop}
                        canMovedCheckboxLeftOnExpand
                        canSelect={!oneShop}
                        customSelect
                        setSelectedRows={setSelectedRows}
                        canResize
                        showGlobalFilter
                        showFilter
                        setLocalFilterActive={setLocalFilterActive}
                        filterActive={filterActive}
                        actionColumn={() => <></>}
                        arrayOfCustomColumns={arrayOfCustomColumns}
                        elevationTable={1}
                        minHeight={oneShop ? "500px" : "450px"}
                        maxHeight={oneShop ? "500px" : "450px"}
                        name="foodCourt"
                        setData={setDataIsUpdated}
                    />
                    {isAddFoodCourtClicked ? (
                        <FoodCourtForm
                            setIsAddFoodCourtClicked={setIsAddFoodCourtClicked}
                            isConsult={isConsult}
                            setIsConsult={setIsConsult}
                            shopId={
                                oneShop
                                    ? shopID
                                    : rowData.shopId !== undefined
                                    ? rowData.shopId
                                    : selectedRows[0] !== undefined
                                    ? selectedRows[0].shopId
                                    : undefined
                            }
                            editedData={rowData}
                        />
                    ) : null}
                </div>
                {isActionsClicked === true ? (
                    <ConfirmationUpdatingDataMessageModal
                        isModalOpened={isActionsClicked}
                        setIsModalOpened={setIsActionsClicked}
                        validationAction={validationAction}
                        actions={actions}
                        setIsEdited={setIsEdited}
                    />
                ) : null}
                {isModalOpened === true ? (
                    <HoursModal
                        isModalOpened={isModalOpened}
                        setIsModalOpened={setIsModalOpened}
                        rowData={rowData}
                    />
                ) : null}
            </div>
        </React.Fragment>
    );
}
